import React, { useState, useEffect } from 'react';

import { Dropdown } from 'react-bootstrap';
import {
    GetWellheadsByJobId_getWellheadsByJobId as Wellheads,
    GetWellheadsByStageGroupId_getWellheadsByStageGroupId as StageWellheads,
    GetMaxStage,
} from '../../../../types/schemaTypes';
import '../JobStageSummary.scss';
import { blockInvalidChar } from '../../../../components/_common/blockInvalidChar';
import { useLazyQuery } from '@apollo/client/react';
import { GET_MAX_STAGE } from '../../../../graphql/client/queries';
import { Loading } from '../../../_common';

interface StagesToEditProps {
    index: number;
    jobStageId: number;
    wellHead: string;
    stageNum: number;
    stageSheetId: number;
    stageSheetDescrip: string;
    maxStageNum: number;
}

interface EditStageModalRowProps {
    JobId: number;
    index: number;
    wellheads: Wellheads[];
    fracType: string | undefined;
    stageItem?: StageWellheads | undefined;
    stageSheetIds: {
        runsheet1: number;
        runsheet2: number;
    };
    stageCount: number;
    setStageCount: React.Dispatch<React.SetStateAction<number>>;
    stagesToEdit: StagesToEditProps[] | undefined;
    setStagesToEdit: React.Dispatch<React.SetStateAction<StagesToEditProps[] | undefined>>;
    showDelete: boolean;
    wellheadError: number[] | undefined;
    setWellheadError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    stageNumberNotAnIntegerError: number[] | undefined;
    setStageNumberNotAnIntegerError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    stageNumberOutOfBoundsError: number[] | undefined;
    setStageNumberOutOfBoundsError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    stageNumberDuplicateError: number[] | undefined;
    setStageNumberDuplicateError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    wellheadDuplicateError: number[] | undefined;
    setWellheadDuplicateError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    runsheetDuplicateError: number[] | undefined;
    setRunsheetDuplicateError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    stageNumberMaxError: number[] | undefined;
    setStageNumberMaxError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
}

const EditStageModalRow = ({
    JobId,
    index,
    wellheads,
    fracType,
    stageItem,
    stageSheetIds,
    stagesToEdit,
    setStagesToEdit,
    stageCount,
    setStageCount,
    showDelete,
    wellheadError,
    setWellheadError,
    stageNumberNotAnIntegerError,
    setStageNumberNotAnIntegerError,
    stageNumberOutOfBoundsError,
    setStageNumberOutOfBoundsError,
    stageNumberDuplicateError,
    setStageNumberDuplicateError,
    wellheadDuplicateError,
    setWellheadDuplicateError,
    runsheetDuplicateError,
    setRunsheetDuplicateError,
    stageNumberMaxError,
    setStageNumberMaxError,
}: EditStageModalRowProps): React.ReactElement => {
    const [wellHead, setWellhead] = useState<string>(stageItem ? stageItem.wellhead : '');
    const [stageNumbers, setStageNumber] = useState(stageItem ? stageItem.stage : -1);
    const [maxStageNumber, setMaxStageNumber] = useState(stageItem ? stageItem.stage : -1);

    const [runsheet, setRunsheet] = useState<string>(
        stageItem ? (stageItem.stageSheetDescrip === 'RunSheet1' ? 'Runsheet 1' : 'Runsheet 2') : 'Runsheet 1',
    );
    const [isDeleted, setIsDeleted] = useState(false);

    const stageToEdit = {
        index: index,
        jobStageId: stageItem?.jobStageId ?? -1,
        wellHead: stageItem?.wellhead ?? '',
        stageNum: stageItem?.stage ?? -1,
        stageSheetId: stageItem?.stageSheetId ?? -1,
        stageSheetDescrip: stageItem?.stageSheetDescrip === 'RunSheet1' ? 'Runsheet 1' : 'Runsheet 2',
        maxStageNum: stageItem?.stage ?? -1,
    };

    useEffect(() => {
        setStagesToEdit(prev => {
            if (!prev) return [stageToEdit];
            if (prev.some(s => s.index === index)) return prev;
            return [...prev, stageToEdit];
        });
    }, []);

    const errorMessage = {
        wellHeadErrorMessage: 'Select a wellhead',
        stageNumberErrorMessage: 'Enter a valid stage number',
        stageNumberOutOfBoundErrorMessage: 'Stage number must not be more than ',
        stageNumberMaxErrorMessage: 'Stage # cannot be more than ',
        stageNumberDuplicateMessage: 'Wellhead - Stage # combination must be unique',
        wellheadDuplicateErrorMessage: 'Wellhead must be unique',
        runnerNumberDuplicateMessage: 'Wellhead - Stage # combination must be unique',
    };

    const getStageCount = () => {
        for (const wellhead of wellheads) {
            if (wellhead && wellhead.wellHead === wellHead) return wellhead.stages;
        }
    };
    const handleOnChangeStageNumber = (stageNumber, maxStage) => {
        if (Number.isNaN(stageNumber) || !Number.isInteger(stageNumber) || stageNumber === 0) stageNumber = -1;

        setStageNumber(stageNumber);

        setStagesToEdit(prev =>
            prev?.map(stage =>
                stage.index === index && stageNumber
                    ? { ...stage, stageNum: stageNumber, maxStageNum: maxStage }
                    : stage,
            ),
        );

        if (stageNumberNotAnIntegerError?.includes(index)) {
            stageNumberNotAnIntegerError.splice(stageNumberNotAnIntegerError.indexOf(index), 1);
            setStageNumberNotAnIntegerError(stageNumberNotAnIntegerError);
        }
        if (stageNumberOutOfBoundsError?.includes(index)) {
            stageNumberOutOfBoundsError.splice(stageNumberOutOfBoundsError.indexOf(index), 1);
            setStageNumberOutOfBoundsError(stageNumberOutOfBoundsError);
        }
        if (stageNumberMaxError?.includes(index)) {
            stageNumberMaxError.splice(stageNumberMaxError.indexOf(index), 1);
            setStageNumberMaxError(stageNumberMaxError);
        }
        if (stageNumberDuplicateError?.includes(index)) {
            stageNumberDuplicateError.splice(stageNumberDuplicateError.indexOf(index), 1);
            setStageNumberDuplicateError(stageNumberDuplicateError);
        }

        if (wellheadDuplicateError?.includes(index)) {
            wellheadDuplicateError.splice(wellheadDuplicateError.indexOf(index), 1);
            setWellheadDuplicateError(wellheadDuplicateError);
        }
        if (runsheetDuplicateError?.includes(index)) {
            runsheetDuplicateError.splice(runsheetDuplicateError.indexOf(index), 1);
            setRunsheetDuplicateError(runsheetDuplicateError);
        }
    };

    const [getParamsForGetMaxStage, { data: getMaxStageData, loading: getMaxStageLoading }] = useLazyQuery<GetMaxStage>(
        GET_MAX_STAGE,
        { fetchPolicy: 'no-cache' },
    );

    useEffect(() => {
        if (getMaxStageLoading || !getMaxStageData?.getMaxStage) return;
        const count = getMaxStageData.getMaxStage.count;
        const next = count + 1;
        setMaxStageNumber(next);
        handleOnChangeStageNumber(next, next);
    }, [getMaxStageData, getMaxStageLoading]);
    const handleOnChangeWellhead = wellhead => {
        setWellhead(wellhead);
        setStagesToEdit(prev =>
            prev?.map(stage =>
                stage.index === index ? { ...stage, wellHead: wellhead } : stage,
            ),
        );

        if (wellheadError?.includes(index)) {
            wellheadError.splice(wellheadError.indexOf(index), 1);
            setWellheadError(wellheadError);
        }
        getParamsForGetMaxStage({
            variables: {
                jobId: JobId,
                wellHead: encodeURIComponent(wellhead),
            },
        });
    };

    const handleOnChangeRunsheet = runsheet => {
        setRunsheet(runsheet);

        setStagesToEdit(prev =>
            prev?.map(stage =>
                stage.index === index && runsheet
                    ? {
                          ...stage,
                          stageSheetDescrip: runsheet,
                          stageSheetId: runsheet === 'Runsheet 1' ? stageSheetIds.runsheet1 : stageSheetIds.runsheet2,
                      }
                    : stage,
            ),
        );
    };

    const handleOnDelete = () => {
        setIsDeleted(true);

        setStageCount(stageCount - 1);

        setStagesToEdit(prev => prev?.filter(stage => stage.index !== index));
    };

    useEffect(() => {
        if (fracType != 'Dual' && runsheet !== 'Runsheet 1') {
            handleOnChangeRunsheet('Runsheet 1');
        }
    }, [fracType]);

    if (isDeleted) {
        return <></>;
    }

    return (
        <tr>
            <td className="AddStageModal-Column">
                <Dropdown>
                    <Dropdown.Toggle
                        disabled={stageItem?.status === 'Completed' ? true : false}
                        variant="outline-dark"
                        id="dropdown-basic"
                        className="AddStageModalRow-Dropdown"
                    >
                        <span>{wellHead.length !== 0 ? wellHead : 'Select Wellhead'}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {wellheads.map((wellhead, index) => (
                            <Dropdown.Item
                                key={index}
                               // onSelect={() => wellhead.wellHead && handleOnChangeWellhead(wellhead.wellHead)}
                               onClick={() => wellhead.wellHead && handleOnChangeWellhead(wellhead.wellHead)}
                            >
                                {wellhead.wellHead}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                    <div className="AddStageModal-Error">
                        {wellheadError?.includes(index) && errorMessage.wellHeadErrorMessage}
                    </div>
                    <div className="AddStageModal-Error">
                        {wellheadDuplicateError?.includes(index) && errorMessage.wellheadDuplicateErrorMessage}
                    </div>
                </Dropdown>
            </td>
            <td
                className={`AddStageModal-Column AddStageNumber-Container ${stageItem?.status === 'Completed' &&
                    'AddStageNumber-Container-Disabled'}`}
            >
                {getMaxStageLoading && <Loading />}
                <input
                    type="number"
                    // min={0}
                    onKeyDown={blockInvalidChar}
                    disabled={stageItem?.status === 'Completed' ? true : false}
                    //disabled={true}
                    className="AddStageNumber"
                    value={stageNumbers === -1 ? '' : stageNumbers}
                    onChange={element => handleOnChangeStageNumber(parseInt(element.target.value), maxStageNumber)}
                />
                <div className="AddStageModal-Error">
                    {stageNumberNotAnIntegerError?.includes(index) && errorMessage.stageNumberErrorMessage}
                </div>
                <div className="AddStageModal-Error">
                    {stageNumberOutOfBoundsError?.includes(index) &&
                        errorMessage.stageNumberOutOfBoundErrorMessage + getStageCount()}
                </div>
                <div className="AddStageModal-Error">
                    {stageNumberMaxError?.includes(index) && errorMessage.stageNumberMaxErrorMessage + maxStageNumber}
                </div>
                <div className="AddStageModal-Error">
                    {stageNumberDuplicateError?.includes(index) && errorMessage.stageNumberDuplicateMessage}
                </div>
            </td>
            <td className="AddStageModal-Column">
                <Dropdown>
                    <Dropdown.Toggle
                        disabled={stageItem?.status === 'Completed' ? true : false}
                        variant="outline-dark"
                        id="dropdown-basic"
                        className="AddStageModalRow-Dropdown"
                    >
                        <span>{runsheet ?? 'Runsheet 1'}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {fracType === 'Dual' ? (
                            <>
                                <Dropdown.Item 
                                //onSelect={() => handleOnChangeRunsheet('Runsheet 1')}
                                onClick={() => handleOnChangeRunsheet('Runsheet 1')}
                                >
                                    Runsheet 1
                                </Dropdown.Item>
                                <Dropdown.Item 
                               // onSelect={() => handleOnChangeRunsheet('Runsheet 2')}
                               onClick={() => handleOnChangeRunsheet('Runsheet 2')}
                                >
                                    Runsheet 2
                                </Dropdown.Item>
                            </>
                        ) : (
                            <Dropdown.Item 
                            //onSelect={() => handleOnChangeRunsheet('Runsheet 1')}
                            onClick={() => handleOnChangeRunsheet('Runsheet 1')}
                            >
                                Runsheet 1
                            </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
                <div className="AddStageModal-Error">
                    {runsheetDuplicateError?.includes(index) && errorMessage.runnerNumberDuplicateMessage}
                </div>
            </td>
            <td className="AddStageModal-Column-Sm">
                {(!stageItem?.status || stageItem?.status === 'Preparing') && showDelete && (
                    <div className="AddStage-Delete-Container">
                        <i className="material-icons AddStage-Delete-Button" onClick={() => handleOnDelete()}>
                            cancel
                        </i>
                    </div>
                )}
            </td>
        </tr>
    );
};

export default EditStageModalRow;
